// @systeme: interface
// Les deux petites questions du rétro-planning, posées au bon moment et jamais imposées :
//  - quand la date d'un événement préparé change : « décaler aussi les étapes ? »
//    (le serveur ne décale jamais en silence) ;
//  - quand une étape est cochée : « c'était le bon moment ? » (trois boutons, ou rien).
import { useState, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { AvisJalon, Element, Retroplanning } from '@foyer/shared';
import { api } from '../lib/api';
import { formaterJourLong, jourDe } from '../lib/dates';
import { useMessages } from './Messages';

export const LIBELLES_AVIS: { valeur: AvisJalon; libelle: string }[] = [
  { valeur: 'trop_tot', libelle: 'Trop tôt' },
  { valeur: 'bien', libelle: 'Pile' },
  { valeur: 'trop_tard', libelle: 'Trop tard' },
];

type Question =
  | { type: 'aucune' }
  | { type: 'decaler'; preparation: Retroplanning }
  | { type: 'avis'; jalonId: string; titre: string };

interface Options {
  /** Rappelé après un décalage ou un avis, pour recharger l'écran. */
  recharger(): Promise<void>;
}

export function useDialoguesPreparation({ recharger }: Options) {
  const { dire, direErreur } = useMessages();
  const [question, setQuestion] = useState<Question>({ type: 'aucune' });
  const [enCours, setEnCours] = useState(false);
  const fermer = () => setQuestion({ type: 'aucune' });

  /** Après une correction ou un report : si c'est une date-pivot et qu'elle a bougé, on demande. */
  async function apresDateChangee(element: Element): Promise<void> {
    if (element.parentId !== null) return;
    try {
      const preparation = await api.retroplannings.deElement(element.id);
      if (preparation?.aDecaler) setQuestion({ type: 'decaler', preparation });
    } catch {
      // Pas grave : la préparation le signalera elle-même.
    }
  }

  /** Après un « fait » sur une étape de préparation : la question du bon moment. */
  async function apresFait(element: Element): Promise<void> {
    if (element.origine !== 'retroplanning' || element.parentId === null) return;
    try {
      const preparation = await api.retroplannings.deElement(element.parentId);
      const jalon = preparation?.jalons.find((j) => j.element.id === element.id);
      if (jalon && jalon.avis === null)
        setQuestion({ type: 'avis', jalonId: jalon.id, titre: element.titre });
    } catch {
      // Pas grave : la question se retrouve sur l'écran de la préparation.
    }
  }

  async function agir(action: () => Promise<unknown>, confirmation: string) {
    setEnCours(true);
    try {
      await action();
      dire(confirmation);
      fermer();
      await recharger();
    } catch (e) {
      direErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  const dialogues: ReactNode = (
    <>
      <Dialog open={question.type === 'decaler'} onClose={fermer} fullWidth maxWidth="xs">
        {question.type === 'decaler' && (
          <>
            <DialogTitle>La date a bougé</DialogTitle>
            <DialogContent>
              <Typography>
                « {question.preparation.pivot.titre} », c’est maintenant le{' '}
                {question.preparation.pivot.debutLe
                  ? formaterJourLong(jourDe(question.preparation.pivot.debutLe))
                  : '…'}
                . Je décale aussi les{' '}
                {question.preparation.jalons.filter((j) => j.element.statut !== 'fait').length}{' '}
                étapes qui restent ?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={fermer} color="inherit" disabled={enCours}>
                Laisser
              </Button>
              <Button
                variant="contained"
                disabled={enCours}
                onClick={() =>
                  void agir(
                    () => api.retroplannings.propager(question.preparation.id),
                    'Les étapes sont décalées.',
                  )
                }
              >
                Décaler
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Dialog open={question.type === 'avis'} onClose={fermer} fullWidth maxWidth="xs">
        {question.type === 'avis' && (
          <>
            <DialogTitle>C’était le bon moment ?</DialogTitle>
            <DialogContent>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                « {question.titre} » — ça nous aide à mieux placer cette étape pour tout le monde.
              </Typography>
              <Stack direction="row" spacing={1}>
                {LIBELLES_AVIS.map((a) => (
                  <Button
                    key={a.valeur}
                    variant="outlined"
                    fullWidth
                    disabled={enCours}
                    onClick={() =>
                      void agir(
                        () => api.retroplannings.donnerAvis(question.jalonId, a.valeur),
                        'Merci, c’est noté.',
                      )
                    }
                  >
                    {a.libelle}
                  </Button>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={fermer} color="inherit">
                Passer
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );

  return { apresDateChangee, apresFait, dialogues };
}
